import React, { useState } from 'react';
import Banner from '../../sections/Banner';
import Header from '../../sections/Header';
import Footer from '../../sections/Footer';
import SectionHeader from '../../composites/SectionHeader';
import FeatureCard from '../../composites/FeatureCard';
import TabSwitcher from '../../composites/TabSwitcher';
import Breadcrumbs from '../../sections/Breadcrumbs';
import { UserGroupIcon, ScaleIcon, Cog6ToothIcon, BookOpenIcon, BeakerIcon } from '@heroicons/react/24/outline';

const About: React.FC = () => {
  const [activeTab, setActiveTab] = useState('client-value');

  const tabs = [
    { id: 'client-value', label: 'Client value' },
    { id: 'ie-value', label: '(i.e.,) value' },
    { id: 'your-growth', label: 'Your Growth' },
  ];

  const clientValueCards = [
    {
      title: 'Individual Consultant',
      description: 'Amplify the impact of each consultant.',
      customImage: '/Maker.png',
    },
    {
      title: 'Case team',
      description: 'Position (i.e.,) as the leading AI transformation partner, driving innovation and sustainable growth across all business functions.',
      customImage: '/Manager.png',
    },
    {
      title: '(i.e.,) Offering:',
      description: 'Develop proprietary IP that enables (i.e.,) to deliver differentiated, leapfrog solutions across Advisory, Practices and Studio. ',
      customImage: '/Master.png',
    },
  ];

  const ieValueCards = [
    {
      title: 'Increase RoR',
      description: 'Increase the average return on role (RoR) by embedding AI Lab into our operating and commercial model.',
      customImage: '/Maker.png',
    },
    {
      title: 'Leapfrog Operating Model',
      description: 'Develop a leapfrog operating model and suite of offerings that enable (i.e.,) to scale impact and further differentiate our value proposition.',
      customImage: '/Manager.png',
    },
  ];

  const yourGrowthCards = [
    {
      title: 'AI Fluency Uplift',
      description: 'Uplift and align AI fluency for every individual—empowering everyone to work smarter, faster, and more creatively.',
      customImage: '/Master.png',
    },
  ];

  const HorizontalCard: React.FC<{ card: any; idx: number }> = ({ card, idx }) => (
    <div 
      className="bg-white rounded-lg p-6 border border-gray-100"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-16 h-16 bg-gray-50 rounded-lg p-2 flex items-center justify-center">
          <img 
            src={card.customImage} 
            alt={card.title} 
            className="object-contain w-full h-full" 
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {card.title}
          </h3>
          <p className="text-gray-700 leading-relaxed">
            {card.description}
          </p>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'client-value':
        return (
          <div className="space-y-4">
            {clientValueCards.map((card, idx) => (
              <HorizontalCard key={card.title} card={card} idx={idx} />
            ))}
          </div>
        );
      case 'ie-value':
        return (
          <div className="space-y-6">
            {ieValueCards.map((card, idx) => (
              <HorizontalCard key={card.title} card={card} idx={idx} />
            ))}
          </div>
        );
      case 'your-growth':
        return (
          <div className="space-y-6">
            {yourGrowthCards.map((card, idx) => (
              <HorizontalCard key={card.title} card={card} idx={idx} />
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Header />
      <Banner
        breadcrumbs={<Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'About' }]} />}
        title="About AI Lab"
        subtitle="Learn about our vision, how we work, and how you can get involved in shaping the future of AI at (i.e.,)."
        colorVariant="gradient"
        icon={
          <div className="flex items-center justify-center w-full h-full">
            <img src="/logos/About.svg" alt="Ecosystem Graphic" className="w-full h-64 object-contain" />
          </div>
        }
        paddingVariant="default"
        showCTAs={true}
        showDefaultGraphic={true}
      />

      {/* Picture of Success Section */}
      <div className="bg-white w-full">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <SectionHeader
            heading="Picture of Success"
            description="AI Lab exists to identify and incubate technology that augments and advances our human-delivered consulting by building complementary capabilities, creating value across three dimensions of our success:."
          />
          
          <div className="mt-8 flex gap-8">
            {/* Vertical Tabs */}
            <div className="flex-shrink-0">
              <TabSwitcher
                tabs={tabs}
                currentTab={activeTab}
                onTabChange={setActiveTab}
                className="flex flex-col w-64"
                layout="vertical"
              />
            </div>
            
            {/* Tab Content */}
            <div className="flex-1">
              {renderTabContent()}
            </div>
          </div>
          

        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-gray-50 w-full">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <SectionHeader
            heading="How it works"
            description="AI Lab exists to identify and incubate technology that augments and advances our human-delivered consulting by building complementary capabilities. To do this, while respecting that it's no one's full-time job, we're distributing the initiative as flatly as possible, operating across multiple streams that collectively shape our approach:"
          />
          {/* First row: 2 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {/* SteerCo */}
            <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col gap-3">
              <UserGroupIcon className="text-teal-600 w-8 h-8" />
              <h3 className="font-semibold text-lg text-gray-900">SteerCo</h3>
              <div className="text-gray-700 text-sm">
                Our guiding team that sets direction, prioritises initiatives, and ensures alignment with (i.e.,)'s broader strategy.
              </div>
            </div>
            {/* Policies */}
            <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col gap-3">
              <ScaleIcon className="text-teal-600 w-8 h-8" />
              <h3 className="font-semibold text-lg text-gray-900">Policies</h3>
              <div className="text-gray-700 text-sm">
                Principles and guardrails for ethical, effective, and secure use of AI and automation in our work.
              </div>
            </div>
          </div>
          {/* Second row: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {/* Companion Platform */}
            <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col gap-3">
              <Cog6ToothIcon className="text-teal-600 w-8 h-8" />
              <h3 className="font-semibold text-lg text-gray-900">AI Lab Platform</h3>
              <div className="text-gray-700 text-sm">
                A central home for AI Lab tools, assets, and pathways, available through The Hub.
              </div>
            </div>
            {/* Content and Tools */}
            <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col gap-3">
              <BookOpenIcon className="text-teal-600 w-8 h-8" />
              <h3 className="font-semibold text-lg text-gray-900">Content and Tools</h3>
              <ul className="list-disc pl-5 space-y-1 text-gray-700 text-sm">
                <li><strong>AI Literacy and Uplift:</strong> Persona-based training to build confidence and capability with emerging tech.</li>
                <li><strong>3rd Party Tools:</strong> Identifying and onboarding best-in-class external solutions.</li>
                <li><strong>Eco-system Watch:</strong> Tracking AI trends, vendor updates, and emerging opportunities.</li>
              </ul>
            </div>
            {/* Incubator */}
            <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col gap-3">
              <BeakerIcon className="text-teal-600 w-8 h-8" />
              <h3 className="font-semibold text-lg text-gray-900">Incubator</h3>
              <div className="text-gray-700 text-sm">
                A practical framework to propose, test, and scale new AI Lab tools, co-developed with our teams.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How to Get Involved Section */}
      <div className="bg-white w-full">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <SectionHeader
            heading="How to Get Involved"
            description="Ready to start your AI journey? Here are the different ways you can engage with the AI Lab program."
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nominate for SteerCo */}
            <FeatureCard
              title="Nominate for SteerCo"
              description="Help set the direction of our AI capability and strategic priorities. Join our guiding team that sets direction, prioritises initiatives, and ensures alignment with (i.e.,)'s broader strategy."
              customImage="/Master-Corporate_innovation.png"
              link={{
                href: "mailto:companion@ie.com.au?subject=SteerCo Nomination",
                label: "Nominate for Q3 (Jan-March 2026)",
                variant: "secondary",
                size: "sm"
              }}
            />
            
            {/* Be a Champion */}
            <FeatureCard
              title="Be a Champion"
              description="Represent your team or discipline, trial tools, share feedback, and lead local adoption. Available for Master, Maker, and Manager personas."
              customImage="/Manager-Handshake.png"
              link={{
                href: "mailto:companion@ie.com.au?subject=Champion Interest",
                label: "Express Interest",
                variant: "secondary",
                size: "sm"
              }}
            />
            
            {/* Propose an Idea for Incubation */}
            <FeatureCard
              title="Propose an Idea for Incubation"
              description="Got an idea for how AI can improve our work? Let's test it through our practical framework to propose, test, and scale new Companion tools."
              customImage="/Maker-Innovate.png"
              link={{
                href: "/initiative-three/companion-incubator",
                label: "Learn More",
                variant: "secondary",
                size: "sm"
              }}
            />
            
            {/* Join a Build Team */}
            <FeatureCard
              title="Join a Build Team"
              description="Collaborate on real Companion experiments and contribute to the development of tools and IP. Work alongside our teams to co-develop new capabilities."
              customImage="/Master-File.png"
              link={{
                href: "mailto:companion@ie.com.au?subject=Build Team Interest",
                label: "Get Involved",
                variant: "secondary",
                size: "sm"
              }}
            />
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default About; 